import { createContext, useContext, useState, useCallback, ReactNode, useEffect, useRef } from 'react';

export interface UploadProgressItem {
  id: string;
  fileName: string;
  status: 'pending' | 'processing' | 'uploading' | 'queued' | 'submitting' | 'submitted' | 'completed' | 'skipped' | 'ERROR';
  message?: string;
  bookTitle?: string;
  bookAuthor?: string;
}

export interface BulkUploadProgress {
  isActive: boolean;
  items: UploadProgressItem[];
  total: number;
  completed: number;
  errors: number;
}

interface BulkUploadContextType {
  progress: BulkUploadProgress;
  startUpload: (fileNames: string[]) => void;
  updateItem: (fileName: string, updates: Partial<UploadProgressItem>) => void;
  completeUpload: () => void;
  isExpanded: boolean;
  toggleExpanded: () => void;
}

const BulkUploadContext = createContext<BulkUploadContextType | undefined>(undefined);

const AUTO_CLEAR_DELAY = 5000; // 5 seconds

export const BulkUploadProvider = ({ children }: { children: ReactNode }) => {
  const [progress, setProgress] = useState<BulkUploadProgress>({
    isActive: false,
    items: [],
    total: 0,
    completed: 0,
    errors: 0,
  });
  const [isExpanded, setIsExpanded] = useState(false);
  const autoClearTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const clearAutoClearTimeout = useCallback(() => {
    if (autoClearTimeoutRef.current) {
      clearTimeout(autoClearTimeoutRef.current);
      autoClearTimeoutRef.current = null;
    }
  }, []);

  const scheduleAutoClear = useCallback(() => {
    clearAutoClearTimeout();
    autoClearTimeoutRef.current = setTimeout(() => {
      setProgress((prev) => ({
        ...prev,
        isActive: false,
        items: [],
        total: 0,
        completed: 0,
        errors: 0,
      }));
      setIsExpanded(false);
      autoClearTimeoutRef.current = null;
    }, AUTO_CLEAR_DELAY);
  }, [clearAutoClearTimeout]);

  useEffect(() => {
    // Check if all items are completed, skipped, or have errors
    if (progress.isActive && progress.total > 0) {
      const allDone = progress.completed + progress.errors === progress.total;
      if (allDone && progress.completed > 0) {
        // Only auto-clear if at least one item was completed/skipped
        scheduleAutoClear();
      } else if (!allDone) {
        clearAutoClearTimeout();
      }
    }
  }, [progress.completed, progress.errors, progress.total, progress.isActive, scheduleAutoClear, clearAutoClearTimeout]);

  useEffect(() => {
    return () => {
      clearAutoClearTimeout();
    };
  }, [clearAutoClearTimeout]);

  const startUpload = useCallback((fileNames: string[]) => {
    clearAutoClearTimeout();
    
    const items: UploadProgressItem[] = fileNames.map((fileName, index) => ({
      id: `upload-${Date.now()}-${index}`,
      fileName,
      status: 'pending',
    }));

    setProgress({
      isActive: true,
      items,
      total: items.length,
      completed: 0,
      errors: 0,
    });
    setIsExpanded(true); // Auto-expand when upload starts
  }, [clearAutoClearTimeout]);

  const updateItem = useCallback((fileName: string, updates: Partial<UploadProgressItem>) => {
    setProgress((prev) => {
      const updatedItems = prev.items.map((item) =>
        item.fileName === fileName ? { ...item, ...updates } : item
      );

      // Count completed (includes submitted, completed, skipped) and errors
      const completed = updatedItems.filter((item) => 
        item.status === 'submitted' || 
        item.status === 'completed' ||
        item.status === 'skipped'
      ).length;
      const errors = updatedItems.filter((item) => item.status === 'ERROR').length;

      return {
        ...prev,
        items: updatedItems,
        completed,
        errors,
      };
    });
  }, []);

  const completeUpload = useCallback(() => {
    clearAutoClearTimeout();
    setProgress((prev) => ({
      ...prev,
      isActive: false,
      items: [],
      total: 0,
      completed: 0,
      errors: 0,
    }));
    setIsExpanded(false);
  }, [clearAutoClearTimeout]);

  const toggleExpanded = useCallback(() => {
    setIsExpanded((prev) => !prev);
  }, []);

  return (
    <BulkUploadContext.Provider
      value={{ progress, startUpload, updateItem, completeUpload, isExpanded, toggleExpanded }}
    >
      {children}
    </BulkUploadContext.Provider>
  );
};

export const useBulkUpload = () => {
  const context = useContext(BulkUploadContext);
  if (context === undefined) {
    throw new Error('useBulkUpload must be used within a BulkUploadProvider');
  }
  return context;
};
